// src/components/error-boundary.tsx
// Componente ErrorBoundary para capturar errores de React y mostrar UI de fallback

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Actualiza el estado para que el siguiente render muestre la UI de fallback
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log del error para debugging
    console.error('ErrorBoundary capturó un error:', error);
    console.error('Información del componente:', errorInfo.componentStack);

    this.setState({ errorInfo });

    // Callback opcional para reportar el error
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Si se proporcionó un fallback personalizado, usarlo
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // UI de error por defecto
      return (
        <div className="error-boundary-container" style={styles.container}>
          <div className="card shadow-lg" style={styles.card}>
            <div className="card-body text-center p-5">
              <div style={styles.iconWrapper}>
                <i className="fa-solid fa-triangle-exclamation fa-4x text-warning" aria-hidden="true"></i>
              </div>

              <h2 className="mt-4 mb-3" style={styles.title}>
                ¡Algo salió mal!
              </h2>

              <p className="text-muted mb-4" style={styles.description}>
                Ha ocurrido un error inesperado. Nuestro equipo ha sido notificado.
                <br />
                Por favor, intenta recargar la página o volver al inicio.
              </p>

              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={this.handleReload}
                  aria-label="Recargar página"
                >
                  <i className="fa-solid fa-rotate-right me-2" aria-hidden="true"></i>
                  Recargar Página
                </button>

                <button
                  className="btn btn-outline-secondary btn-lg"
                  onClick={this.handleGoHome}
                  aria-label="Ir al inicio"
                >
                  <i className="fa-solid fa-home me-2" aria-hidden="true"></i>
                  Ir al Inicio
                </button>
              </div>

              {/* Mostrar detalles del error en desarrollo */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="mt-4 text-start">
                  <details className="bg-light p-3 rounded">
                    <summary className="text-danger cursor-pointer" style={{ cursor: 'pointer' }}>
                      <i className="fa-solid fa-bug me-2" aria-hidden="true"></i>
                      Detalles técnicos (solo desarrollo)
                    </summary>
                    <pre className="mt-3 text-danger small" style={styles.errorDetails}>
                      {this.state.error.toString()}
                      {this.state.errorInfo && (
                        <>
                          {'\n\nComponent Stack:'}
                          {this.state.errorInfo.componentStack}
                        </>
                      )}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          </div>

          {/* Logo y branding */}
          <div className="mt-4 text-center text-muted">
            <img
              src="/img/logo.png"
              alt="AMPI Riviera Nayarit"
              style={{ maxWidth: '120px', opacity: 0.6 }}
            />
            <p className="small mt-2">
              AMPI Riviera Nayarit - Portal Inmobiliario
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Estilos inline para evitar dependencias de CSS
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8f9fa',
    padding: '20px'
  },
  card: {
    maxWidth: '600px',
    width: '100%',
    borderRadius: '16px',
    border: 'none'
  },
  iconWrapper: {
    width: '80px',
    height: '80px',
    backgroundColor: '#fff3cd',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto'
  },
  title: {
    color: '#212529',
    fontWeight: 600
  },
  description: {
    fontSize: '1.1rem',
    lineHeight: 1.6
  },
  errorDetails: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '300px',
    overflow: 'auto',
    backgroundColor: '#f8d7da',
    padding: '15px',
    borderRadius: '8px',
    fontSize: '12px'
  }
};

export default ErrorBoundary;
